import { trackUserBehavior, UNIQ_BEHAVIOR } from '@rendora/common/utils/report';
import { clear, del, get, getMany, set } from 'idb-keyval';
import { addOpus } from '~/api';
import { ACTOR_NAV, CONFIG_NAV, CONTENT_TABS, RESOLUTION, type SUB_NAV } from '~/constants';
import { SESSION_KEY } from '~/constants/config';
import type { EditorJumpInfo } from '~/utils/localStorage';

const DB = { set, get, del, clear, getMany };

/**
 * 创建作品，并跳转到编辑器
 * @param resolution
 * @param source
 */
async function _createWork(resolution: RESOLUTION) {
  const resource = injectResourceLocation();

  const route = useRoute();
  // 创建作品
  if (resolution) {
    const res = await addOpus({
      resolution,
    });
    trackEvent(TRACK_EVENT.BOOT_EDITOR, {
      source: resource?.source,
      page: route.name,
    });
    window.location.href = `/editor/${res?.data.id}/${res?.data.secret_id}`;
  }
}

function getDimension(resolution: RESOLUTION | undefined) {
  if (!resolution) return CREATE_OPUS.DEFAULT;
  if (resolution === RESOLUTION.HORIZONTAL) return CREATE_OPUS.HORIZONTAL;
  return CREATE_OPUS.VERTICAL;
}

/**
 * 创建作品,并跳转3D生成-场景
 * @param resolution
 * @param source
 */
export const createWorkAndJumpToStudio = async (resolution: RESOLUTION) => {
  const resource = injectResourceLocation();
  const route = useRoute();

  if (resource?.source) {
    trackEvent(TRACK_EVENT.YOUYAN_PLATFORM_CREATE_NEW_CLICK, {
      source: resource.source,
      dimension: getDimension(resolution),
    });
  }

  sendEditorInfo({
    nav: CONFIG_NAV.CONTENT,
    tab: CONTENT_TABS.STUDIO,
    report: {
      page: route.name,
    },
  });

  // 创建作品
  _createWork(resolution);
};

/**
 * 创建作品
 * @param resolution
 * @param source
 */
export const createWorkAndJumpToCharacter = async (resolution: RESOLUTION) => {
  const route = useRoute();

  sendEditorInfo({
    nav: CONFIG_NAV.ACTOR,
    report: {
      page: route.name,
    },
  });

  // 创建作品
  _createWork(resolution);
};

/**
 * 通过数字人创建作品，并跳转对应模块
 * @param characterId
 * @param navKey
 * @param subKey
 */
export const createWorkByCharacter = async <T extends keyof SUB_NAV>(
  characterId: number,
  editorJumpInfo: EditorJumpInfo<T>,
) => {
  const resource = injectResourceLocation();
  try {
    const route = useRoute();
    const link = location.pathname + location.search;
    const tab = editorJumpInfo.tab as any;
    sendEditorInfo({
      nav: editorJumpInfo.nav || CONFIG_NAV.BEGIN,
      tab: {
        ...tab,
        lastCharacter: characterId,
      },
      meta: {
        link,
      },
    });

    trackEvent(TRACK_EVENT.BOOT_EDITOR, {
      page: route.name,
      source: resource?.source,
    });

    switch (editorJumpInfo.nav) {
      case CONFIG_NAV.ACTOR:
        trackUserBehavior(UNIQ_BEHAVIOR.CLICK_EDITOR_CHAR, {
          page: route.name,
          source: resource?.source,
        });
        window.open(
          `/editor/char?character_id=${characterId}&is_custom_config=false&need_back=false`,
          '_blank',
        );
        break;
      case CONFIG_NAV.CONTENT:
        window.open(
          `/editor/get-started?id=1&character_id=${characterId}&is_custom_character_config=false`,
          '_blank',
        );
        break;
      default:
        break;
    }
  } catch (e) {
    console.error(e);
  }
};

export const gotoEmptyEditor = () => {
  // 新建作品选择页面改为编辑器处理
  const resource = injectResourceLocation();
  const route = useRoute();

  trackEvent(TRACK_EVENT.BOOT_EDITOR, {
    source: resource?.source || SOURCE.HEADER,
    page: route.name,
  });

  const lastCharacter = sessionGetItem(SESSION_KEY.LAST_CHARACTER);
  const link = location.pathname + location.search;

  sendEditorInfo({
    nav: CONFIG_NAV.BEGIN,
    tab: {
      link,
      lastCharacter,
    },
    report: {
      page: route.name,
    },
  });

  window.location.href = '/editor/get-started?id=1';
};

export enum GET_STARTED_TYPE {
  FROM_ZERO = 1,
  FROM_TEMPLATE = 2,
  FROM_AI = 3,
  FROM_PPT = 4,
}

// 场景详情页面跳转到编辑器
export const useCharacterOps = () => {
  /**
   * edit(编辑人物) create（用它创作） renewal（人物焕新）
   * @param type edit | create | renewal
   * @param character
   *
   * /editor/avatar  这个是原可直接进入造人的页面，但是带左侧导航的
   * /editor/char 进入编辑器造人页面，不带左侧导航的
   *
   */
  const route = useRoute();
  const createOps = (
    type?: 'edit' | 'create' | 'renewal' | 'char3D',
    character?: {
      id?: number;
      is_custom_config?: boolean;
      tab?: GET_STARTED_TYPE;
      charTab?: ACTOR_NAV;
    },
  ) => {
    const isCustomConfig = character?.is_custom_config || false;
    const tabChar = character?.tab || GET_STARTED_TYPE.FROM_ZERO;
    const characterId = character?.id?.toString();
    const link = location.pathname + location.search;
    const domain = location.origin;
    switch (type) {
      case 'char3D': // 个性化3D形象：点击新开页面进入编辑器的人物页面（不展示左侧的侧边栏）
        sendEditorInfo({
          nav: CONFIG_NAV.ACTOR,
          tab: {},
          meta: {
            link,
          },
        });
        trackUserBehavior(UNIQ_BEHAVIOR.CLICK_EDITOR_CHAR, {
          page: route.name,
        });
        window.open(`${domain}/editor/char`, '_blank');
        break;

      case 'renewal': // 人物焕新: 点击人物焕新模块新开页面打开编辑器，同在编辑器中点击人物焕新模块逻辑，进入造人编辑器，默认定位在人物Tab
        sendEditorInfo({
          nav: CONFIG_NAV.ACTOR,
          tab: {
            [ACTOR_NAV.CHARACTER]: true,
          },
          meta: {
            link,
          },
        });
        trackUserBehavior(UNIQ_BEHAVIOR.CLICK_EDITOR_CHAR, {
          page: route.name,
        });
        window.open(
          `${domain}/editor/char?character_id=${characterId}&is_custom_config=${isCustomConfig}&need_back=false`,
          '_blank',
        );
        break;
      case 'edit': // 编辑
        sendEditorInfo({
          nav: CONFIG_NAV.ACTOR,
          tab: {
            [ACTOR_NAV.COLLECTION]: true,
            // lastCharacter: characterId,
          },
          meta: {
            link,
          },
        });
        trackUserBehavior(UNIQ_BEHAVIOR.CLICK_EDITOR_CHAR, {
          page: route.name,
        });
        window.open(
          `${domain}/editor/char?character_id=${characterId}&is_custom_config=${isCustomConfig}`,
          '_blank',
        );
        break;
      case 'create': // 用它创作 从零开始创作
        sendEditorInfo({
          nav: CONFIG_NAV.BEGIN,
          tab: {},
          meta: {
            link, // 所有的link都不带域名
          },
          report: {
            // 可传可不传 编辑器没有用到
            page: route.name || undefined,
          },
        });
        // 人物接口中is_custom_config字段有就传
        window.open(
          `${domain}/editor/get-started?id=${tabChar}&character_id=${characterId}&is_custom_character_config=${isCustomConfig}`,
          '_blank',
        );
        break;
      default:
        if (!character?.charTab) {
          return;
        }
        sendEditorInfo({
          nav: CONFIG_NAV.ACTOR,
          tab: {
            [character?.charTab]: true,
          },
          meta: {
            link,
          },
        });
        trackUserBehavior(UNIQ_BEHAVIOR.CLICK_EDITOR_CHAR, {
          page: route.name,
        });
        window.open(
          `${domain}/editor/char?character_id=${characterId}&is_custom_config=${isCustomConfig}&need_back=false`,
          '_blank',
        );
        break;
    }
  };

  return { createOps };
};

export const useSceneOps = () => {
  const createOps = (
    type: 'edit' | 'create',
    scene: { id: number; is_custom_config?: boolean; tab?: GET_STARTED_TYPE },
  ) => {
    const isCustomConfig = scene?.is_custom_config || false;
    const tabScene = scene?.tab || GET_STARTED_TYPE.FROM_ZERO;
    const sceneId = scene?.id?.toString();
    const link = location.pathname + location.search;
    const domain = location.origin;
    const userInfo = useUserInfo();
    switch (type) {
      case 'create': // 从零开始创作：
        sendEditorInfo({
          nav: CONFIG_NAV.BEGIN,
          tab: {},
          meta: {
            link, // 所有的link都不带域名
          },
          // report: { // 可传可不传 编辑器没有用到
          //   page: route.name || undefined,
          // },
        });
        window.open(
          `${domain}/editor/get-started?id=${tabScene}&is_custom_map_config=${isCustomConfig}&map_id=${sceneId}`,
          '_blank',
        );
        break;
      case 'edit': // 编辑
        sendEditorInfo({
          nav: CONFIG_NAV.CONTENT,
          tab: CONTENT_TABS.STUDIO,
          meta: {
            link, // 所有的link都不带域名
          },
          // report: { // 可传可不传 编辑器没有用到
          //   page: route.name || undefined,
          // },
        });
        if (!userInfo.value) {
          goLogin({
            redirect: encodeURIComponent(
              `${domain}/editor/studio?is_custom_config=${isCustomConfig}&map_id=${sceneId}`,
            ),
          });
          return;
        }
        window.open(
          `${domain}/editor/studio?is_custom_config=${isCustomConfig}&map_id=${sceneId}`,
          '_blank',
        );
        break;
      default:
        break;
    }
  };
  return { createOps };
};

/** 根据预设创建作品，并跳转编辑器 */
export const createWorkWithPreset = async (payload: any) => {
  const route = useRoute();
  const resource = injectResourceLocation();

  const res = await addOpus({
    ...payload.create_data,
  });
  trackEvent(TRACK_EVENT.BOOT_EDITOR, {
    source: resource?.source,
    page: route.name,
  });
  await DB.set('youyan-temp-new-data', payload.youyanTempNewData);
  window.location.href = `/editor/${res?.data.id}/${res?.data.secret_id}`;
};
